import { useEffect, useState } from "react";
import axios from "axios";
import { Clock, X, ChevronLeft, ChevronRight, Heart, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "instituto", label: "Lembranças do Instituto", accent: "text-ga-amber_deep", bar: "#F59E0B" },
  { id: "enaga", label: "Lembranças ENAGA", accent: "text-ga-emerald", bar: "#10B981" },
];

export default function Lembrancas() {
  const [active, setActive] = useState("instituto");
  const [byCat, setByCat] = useState({ instituto: [], enaga: [] });
  const [loading, setLoading] = useState(true);
  const [modalIdx, setModalIdx] = useState(null);

  useEffect(() => {
    axios.get(`${API}/lembrancas`)
      .then((r) => setByCat(r.data.by_category || { instituto: [], enaga: [] }))
      .catch(() => setByCat({ instituto: [], enaga: [] }))
      .finally(() => setLoading(false));
  }, []);

  const items = byCat[active] || [];
  const totalAll = (byCat.instituto?.length || 0) + (byCat.enaga?.length || 0);

  // Hide whole section if empty (non-intrusive — nunca polui a home)
  if (!loading && totalAll === 0) return null;

  const openModal = (idx) => setModalIdx(idx);
  const closeModal = () => setModalIdx(null);
  const nextModal = () => setModalIdx((i) => (i === null ? null : (i + 1) % items.length));
  const prevModal = () => setModalIdx((i) => (i === null ? null : (i - 1 + items.length) % items.length));

  return (
    <section
      id="lembrancas"
      data-testid="lembrancas-section"
      className="section-pad bg-gradient-to-b from-background to-white relative overflow-hidden"
    >
      <div className="absolute top-20 right-0 w-[32rem] h-[32rem] rounded-full bg-ga-amber/10 blur-3xl pointer-events-none" />
      <div className="container-ga relative">
        <div className="max-w-2xl mb-10">
          <div className="pill border-ga-ink/10 text-ga-ink/60 bg-ga-ink/5 mb-4" data-testid="lembrancas-pill">
            <Sparkles className="w-3.5 h-3.5" /> Memória viva
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ga-ink">
            Lembranças que <span className="gradient-text-ga">contam nossa história</span>.
          </h2>
          <p className="mt-5 text-ga-ink/65 text-lg leading-relaxed">
            Momentos antigos dos nossos perfis: eventos, atendimentos, encontros e memórias que continuam vivas no dia a dia do Instituto.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="lembrancas-tabs">
          {TABS.map((t) => {
            const isActive = t.id === active;
            const count = byCat[t.id]?.length || 0;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setActive(t.id)}
                data-testid={`lembrancas-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-semibold transition-colors ${
                  isActive
                    ? "border-ga-ink bg-ga-ink text-white"
                    : "border-border bg-white text-ga-ink/70 hover:border-ga-ink/40"
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                {t.label}
                <span className={`text-[11px] opacity-70 ${isActive ? "text-white/80" : ""}`}>({count})</span>
              </button>
            );
          })}
        </div>

        {/* Grid or fallback */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="aspect-square rounded-2xl bg-ga-ink/5 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div
            className="py-16 text-center rounded-3xl border-2 border-dashed border-ga-ink/10 bg-white"
            data-testid="lembrancas-empty"
          >
            <Heart className="w-8 h-8 text-ga-amber/60 mx-auto mb-3" />
            <div className="font-display font-bold text-ga-ink">Em breve mais lembranças serão adicionadas</div>
            <p className="text-sm text-ga-ink/55 mt-1">
              Estamos curando os melhores momentos deste perfil. Volte em breve.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
            data-testid="lembrancas-grid"
          >
            {items.map((it, idx) => (
              <button
                key={it.id}
                type="button"
                onClick={() => openModal(idx)}
                data-testid={`lembranca-card-${it.id}`}
                className="group relative aspect-square rounded-2xl overflow-hidden bg-ga-ink/5 shadow-sm hover:shadow-xl transition-all"
              >
                <img
                  src={it.thumbnail_url || it.media_url}
                  alt={it.caption || "Lembrança do Instituto"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                {it.caption && (
                  <div className="absolute bottom-0 left-0 right-0 p-3 text-left text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="text-xs font-medium leading-snug line-clamp-2">{it.caption}</div>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {modalIdx !== null && items[modalIdx] && (
        <div
          className="fixed inset-0 z-50 bg-ga-ink/95 flex items-center justify-center p-4"
          onClick={closeModal}
          data-testid="lembranca-modal"
        >
          <button
            onClick={closeModal}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
            data-testid="lembranca-modal-close"
          >
            <X className="w-5 h-5" />
          </button>

          {items.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevModal(); }}
                className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="lembranca-modal-prev"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextModal(); }}
                className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center"
                data-testid="lembranca-modal-next"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}

          <div
            className="max-w-4xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={items[modalIdx].media_url}
              alt={items[modalIdx].caption || "Lembrança"}
              className="max-h-[70vh] w-auto mx-auto object-contain rounded-lg"
            />
            {items[modalIdx].caption && (
              <div className="mt-4 text-white/85 text-center text-sm leading-relaxed max-w-2xl mx-auto">
                {items[modalIdx].caption}
              </div>
            )}
            <div className="mt-2 text-center text-white/50 text-xs">
              {modalIdx + 1} de {items.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
